import React, { useState, useEffect, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import StripeCardElements from '../../../area/booking/components/CardPayment/StripeCardElements';

import arrangeCountries from '../../../utilities/arrangeCountries';
import getFromDataLayer from '../../../utilities/getFromDataLayer';
import dictionaryToTwoDimensionalArray from '../../../area/booking/modelBuilders/dictionaryToTwoDimensionalArray';
import api from '../../../common/services/api';
import bookingApi from '../../../area/booking/services/api';
import withFormValidation from '../../../common/hocs/withFormValidation';
import * as validationService from '../../../area/booking/services/validationService';

import ContributeIntro from './ContributeIntro';
import BillingAddress from './BillingAddress';
import UserContribution from './UserContribution';
import UserDetails from './UserDetails';
import MarketingCheckbox from '../../../area/booking/components/MarketingCheckbox';
import Modal from '../../../common/components/modal/Modal';
import ThankYou from './ThankYou';

import IData from '../interfaces/IData';
import IAddress from '../interfaces/IAddress';
import CardDetails from '../../../area/booking/components/CardPayment/CardDetails';
import { geStripeCurrencyKey } from '../../../utilities/getEnvKey';
import { currencyAvailable } from '../../../area/booking/services/paymentRequestService';


declare global {
    interface Window {
        dataLayer: any;
        // @ts-ignore
        Stripe: any;
    }
};

interface IFormData {
    currency: string;
    message: string;
    creditAmount: string;
    firstName: string;
    lastName: string;
    email: string;
    address: IAddress;
    newsletter: number;
    'payment[name_on_card]': string;
    'payment[type]': string;
};

interface IProps {
    data: IData;
    ctaThankYouLink: string;
    optInMessage: string;
    formErrors: { [key: string]: string }
    onBlurValidation(event: React.ChangeEvent<HTMLFormElement>): void;
    validateForm(form: React.FormEvent<HTMLFormElement>): any;
};

interface CheckBoxChangeEvent {
    (evt: Event): void;
    offerCheckbox: number;
};

const ContributeForm: React.FC<IProps> = ({ data, ctaThankYouLink, optInMessage, onBlurValidation, formErrors, validateForm }) => {
    const { giftlist, currencies, countries, cardtypes } = data;
    const userCurrency = getFromDataLayer(window.dataLayer, 'env').currency;
    const userCountry = getFromDataLayer(window.dataLayer, 'user').sessionCountry;
    const countryList = arrangeCountries(dictionaryToTwoDimensionalArray(countries));
    const currencyList = dictionaryToTwoDimensionalArray(currencies);

    const [stateList, setStateList] = useState<[string, string][]>([]);
    const [pageErrors, updatePageErrors] = useState<string>('');
    const [isProcessingBooking, setIsProcessingBooking] = useState<boolean>(false);
    const [showThankYou, setShowThankYou] = useState(false);
    const [paymentIntent, setPaymentIntent] = useState('');
    const [paymentClientSecret, setPayementClientSecret] = useState('');
    const [SCAChallengeRequired, setSCAChallengeRequired] = useState(false);

    const initialFormData = {
        currency: userCurrency as string,
        message: '',
        creditAmount: '',
        address: {
            billingaddress1: '',
            billingaddress2: '',
            billingcity: '',
            billingpostcode: '',
            billingcountry: userCountry === 'UK' ? 'GB' : userCountry as string,
            billingstate: '',
            billingregion: '',
        },
        firstName: '',
        lastName: '',
        email: '',
        newsletter: 0,
        'payment[name_on_card]': '',
        'payment[type]': ''
    }

    const [formData, setFormData] = useState<IFormData>(initialFormData);

    const doProcessOrder = (event: React.FormEvent<HTMLFormElement>, validateCallback: any) => {
        event.preventDefault();

        const form = (event.target as HTMLFormElement).form;

        validateForm(form).then((errors: any) => {
            const numberOfErrors = Object.keys(errors).reduce((acc, curr) => {
                if (errors[curr] !== '') {
                    acc += 1;
                    updatePageErrors(errors[curr]);
                }
                return acc;
            }, 0)

            if (numberOfErrors === 0) {
                setIsProcessingBooking(true);
                updatePageErrors('');
                return validateCallback(true);
            }
        });
    };

    const buildFormDataAndConfirm = (
        paymentMethodId: string,
        paymentIntentId = '',
    ) => {

        const paymentDetails = {
            payment_intent_id: paymentIntentId,
            payment_method_id: paymentMethodId,
        }
 
        bookingApi.confirmContributionPayment(formData, paymentDetails, giftlist?.code)
            .then((response: any) => {
                if (response.api_response && response.api_response.status === 'requires_confirmation') {
                    // We need to confirm now the payment flow accepted
                    setPaymentIntent(response.api_response.payment_intent_reference);
                    setPayementClientSecret(response.api_response.client_secret);
                    buildFormDataAndConfirm(paymentMethodId, response.api_response.payment_intent_reference);
                    setSCAChallengeRequired(false);
                } else if (response.status === 403) {
                    // SCA FLow
                    setSCAChallengeRequired(true);
                } else if (response.api_response && response.api_response.success) {
                    // Booking successful
                    setIsProcessingBooking(false);
                    setShowThankYou(true);
                    setFormData(initialFormData);
                } else if (response.status === 402) {
                    // Payment was not authorised
                    throw new Error('Payment has not been authorised. Please try again.')
                } else {
                    // Something has gone wrong
                    throw new Error(response.statusText)

                }
            })
            .catch((error: Error) => {
                // Reset the flow for new payment intent flow
                setPaymentIntent('');
                setPayementClientSecret('');
                setSCAChallengeRequired(false);
                setIsProcessingBooking(false);
                updatePageErrors(error.message);
            })
    };

    const getStateListData = (code: string) => {
        api.getStateList(code)
            .then((states) => {
                if (states !== false) {
                    const statesList: [string, string][] = Object.keys(states).reduce<[string, string][]>((acc, cur) => {
                        acc.push([cur, states[cur]]);
                        return acc;
                    }, []);
                    setStateList([...statesList]);
                } else {
                    setStateList([]);
                    setFormData({ ...formData, address: { ...formData.address, billingstate: '' } });
                }
            })
            .catch(console.error);
    };

    useEffect(() => {
        const statefulCountries = ['US', 'CA', 'AU'];

        if (statefulCountries.includes(formData.address.billingcountry)) {
            getStateListData(formData.address.billingcountry);
        }
    }, []);

    const handleChange = (event: React.ChangeEvent<HTMLFormElement>) => {
        const { target: { name, value } } = event;

        if (name.includes('billing')) {
            setFormData({ ...formData, address: { ...formData.address, [name]: value } });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleSelectChange = (event: React.ChangeEvent<HTMLFormElement>) => {
        const { target: { value, name } } = event;
        const statefulCountries = ['US', 'CA', 'AU'];

        if (name.includes('billingcountry') && !statefulCountries.includes(value)) {
            setFormData({ ...formData, address: { ...formData.address, [name]: value, billingstate: '' } });
            setStateList([]);
        } else if (name.includes('country') && statefulCountries.includes(value)) {
            getStateListData(value);
            setFormData({ ...formData, address: { ...formData.address, [name]: value } });
        } else {
            setFormData({ ...formData, address: { ...formData.address, [name]: value } });
        }
    };

    const handleCurrencyChange = (event: React.ChangeEvent<HTMLFormElement>) => {
        const { target: { name, value } } = event;

        // Prevent Chrome from auto completing with empty string
        if (name === 'currency' && value !== '') {
            setFormData({ ...formData, [name]: value });
        }
    };

    const updateFormData = (event: CheckBoxChangeEvent) => {
        setFormData({ ...formData, newsletter: event.offerCheckbox })
    }

    const buildCardTypes = () => {
        return Object.keys(cardtypes).reduce<[string, string][]>((acc, curr) => {
            acc.push([curr, cardtypes[curr]]);
            return acc;
        }, []);
    }

    const getPaymentApiKey = (): string | null => {
        const isCurrencyAvailable = currencyAvailable(formData.currency);
        const stripeCurrencyKey = geStripeCurrencyKey(formData.currency);

        if (isCurrencyAvailable) {
            return stripeCurrencyKey;
        }

        return null;
    }

    const paymentButtonApiKey =getPaymentApiKey();

    const stripePromise = useMemo(() => {
        if (paymentButtonApiKey) {
            return loadStripe(paymentButtonApiKey);
        }
        return null;
    }, [paymentButtonApiKey, formData.currency]);

    const setPlaceholders = () => {
        const cardType = formData['payment[type]'];

        return cardType !== 'AX'
            ? {
                    cardPlaceholder: '0000 0000 0000 0000',
                    cvvPlaceholder: '123',
                    cvvText:
                        'The CVV number is the last 3 digits on the signature strip or embedded on the back of your card',
                }
            : {
                    cardPlaceholder: '0000 000000 00000',
                    cvvPlaceholder: '1234',
                    cvvText: 'The CVV number is the 4 digits printed on front of your card',
                };
    }

    const thankYouProps = {
        recipientName: giftlist?.recipient_name,
        partnerName: giftlist?.second_recipient_name,
        thankYouMessage: giftlist?.thank_you_message
    };

    const bookingError = (): void => {
        setIsProcessingBooking(false);
    };

    const cardTypesArray = buildCardTypes();

    return (
        <>
            <form className="c-contributionForm">
                <ContributeIntro giftList={giftlist} />
                <UserContribution
                    currencies={currencyList}
                    currency={formData.currency}
                    message={formData.message}
                    creditAmount={formData.creditAmount}
                    handleChange={handleChange}
                    handleSelectChange={handleCurrencyChange}
                    handleBlur={onBlurValidation}
                    errors={formErrors}
                />
                <UserDetails
                    firstName={formData.firstName}
                    lastName={formData.lastName}
                    email={formData.email}
                    handleChange={handleChange}
                    handleBlur={onBlurValidation}
                    errors={formErrors}
                />
                <BillingAddress
                    countries={countryList}
                    stateList={stateList}
                    address={formData.address}
                    handleChange={handleChange}
                    handleSelectChange={handleSelectChange}
                    handleBlur={onBlurValidation}
                    errors={formErrors}
                />
                <section className='booking-card'>
                    <div className='cardDetails-container'>
                        <CardDetails
                            errors={formErrors}
                            cardTypesArray={cardTypesArray}
                            paymentType={formData['payment[type]']}
                            handleTypeChange={handleChange}
                            nameOnCard={formData['payment[name_on_card]']}
                            handleNameChange={handleChange}
                            handleBlur={onBlurValidation}
                            showCardType={true}
                        />
                        <Elements stripe={stripePromise} key={formData.currency}>
                            <StripeCardElements
                                doProcessOrder={doProcessOrder}
                                buildFormDataAndConfirm={buildFormDataAndConfirm}
                                bookingError={bookingError}
                                isProcessingBooking={isProcessingBooking}
                                updatePageErrors={updatePageErrors}
                                paymentIntent={paymentIntent}
                                paymentClientSecret={paymentClientSecret}
                                SCAChallengeRequired={SCAChallengeRequired}
                                setPlaceholders={setPlaceholders}                                
                            />
                        </Elements>
                    </div>
                    <div className="card-security flex-row-middle">
                        <p className="card-security-text">
                            We use secure transmission and encrypted storage to protect your personal information.
                        </p>
                        <p className="card-security-logo">
                            <img
                                className="secured-logo"
                                src="/bundles/familysystem/images/secure-payment.svg"
                                alt="secured by ssl logo"
                            />
                        </p>
                    </div>
                </section>
                <div className="p-contributionForm p-contributionForm--checkbox">
                    <MarketingCheckbox
                        offerCheckbox={formData.newsletter}
                        updateFormData={updateFormData}
                        optInMessage={optInMessage}
                    />
                </div>
                {pageErrors && (
                    <section className="pageErrors">
                        {pageErrors}
                    </section>
                )}
            </form>
            {showThankYou && (
                <Modal show={true} closeAndReload={true}>
                    <ThankYou
                        thankYouProps={thankYouProps}
                        ctaThankYouLink={ctaThankYouLink}
                    />
                </Modal>
            )}
        </>
    );
};

export default withFormValidation(ContributeForm, validationService);